import { queryClient } from "./queryClient"

/**
 * Remove all failed (error state) mutations for a given entity from the mutation cache.
 *
 * Matches mutations where `variables.id === entityId`. Does NOT match on
 * `variables.entity_id` — that field references a parent entity (e.g. a photo's
 * observation), and clearing those would destroy unrelated mutations like
 * photo uploads when the parent entity is re-saved.
 *
 * Call this at the top of `onMutate` in every mutation hook so that when the
 * user edits and resubmits, the new mutation cleanly replaces the failed one.
 *
 * Options:
 * - `preserveCreates`: When true, skip CREATE mutations (mutationKey[1] === "create").
 *   Used by UPDATE hooks so the mutationFn's upsert fallback can still access
 *   the CREATE's variables to reconstruct an entity that doesn't exist on the
 *   server yet. The caller is responsible for cleaning up the CREATE later
 *   (typically in onSuccess after the upsert succeeds).
 */
export function removeFailedMutationsForEntity(
  entityId: string,
  { preserveCreates = false }: { preserveCreates?: boolean } = {},
): void {
  const mutations = queryClient.getMutationCache().findAll({
    predicate: (mutation) => {
      if (mutation.state.status !== "error") return false

      const vars = mutation.state.variables as
        | Record<string, unknown>
        | undefined
      if (!vars) return false

      if (vars.id !== entityId) return false

      if (preserveCreates) {
        const key = mutation.options.mutationKey as string[] | undefined
        if (key?.[1] === "create") return false
      }

      return true
    },
  })

  for (const mutation of mutations) {
    queryClient.getMutationCache().remove(mutation)
  }
}
